import { useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"
import { useState } from "react"

import { todayStr as getTodayStr } from "@/lib/date-utils"

export interface TimeEntryData {
  id?: number
  user_id: number | null
  time_mode: "actual" | "block"
  date: string | null
  started_at: string | null
  ended_at: string | null
  admin_minutes: number
  service_item_type_id: number | null
  travel_hours: number
  notes: string
  mileage_to_job: number
  mileage_on_job: number
  mileage_from_job: number
  vehicle_allowance: string
}

interface ServiceItemType {
  id: number
  name: string
  cost: number
  price: number
}

interface TravelHourOption {
  value: number
  label: string
}

interface Props {
  caseId: number
  timeEntry: TimeEntryData
  users: { id: number; name: string }[]
  serviceItemTypes: ServiceItemType[]
  travelHourOptions: TravelHourOption[]
  canViewFinancials: boolean
  canSelectEmployee: boolean
  canUseBlockTime: boolean
  errors?: Record<string, string[]>
  onCancel: () => void
}

// Clock time options
const clockHourOptions = Array.from({ length: 12 }, (_, i) => i + 1)
const clockMinuteOptions = [0, 15, 30, 45]
const blockHourOptions = Array.from({ length: 25 }, (_, i) => i)
const blockMinuteOptions = [0, 15, 30, 45]

function decomposeDateTime(iso: string) {
  // Parse directly from ISO string to avoid browser timezone conversion
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(iso)
  if (!match) {
    // Fallback for unexpected formats
    const d = new Date(iso)
    let h = d.getHours()
    const ampm: "AM" | "PM" = h >= 12 ? "PM" : "AM"
    h = h % 12 || 12
    const m = Math.round(d.getMinutes() / 15) * 15
    return {
      date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      hour: h,
      minute: m % 60,
      ampm,
    }
  }
  const [, date, hourStr, minStr] = match
  let h = parseInt(hourStr)
  const ampm: "AM" | "PM" = h >= 12 ? "PM" : "AM"
  h = h % 12 || 12
  const m = Math.round(parseInt(minStr) / 15) * 15
  return { date, hour: h, minute: m % 60, ampm }
}

function buildDateTime(
  date: string,
  hour: number,
  minute: number,
  ampm: "AM" | "PM",
): string {
  let h24 = hour
  if (ampm === "AM" && hour === 12) h24 = 0
  else if (ampm === "PM" && hour !== 12) h24 += 12
  const hStr = h24.toString().padStart(2, "0")
  const mStr = minute.toString().padStart(2, "0")
  return `${date}T${hStr}:${mStr}:00`
}

export default function TimeEntryForm({
  caseId,
  timeEntry,
  users,
  serviceItemTypes,
  travelHourOptions,
  canViewFinancials,
  canSelectEmployee,
  canUseBlockTime,
  errors,
  onCancel,
}: Props) {
  const isEdit = !!timeEntry.id

  // Time mode
  const [timeMode, setTimeMode] = useState<"actual" | "block">(
    timeEntry.time_mode ?? "actual",
  )

  // Decompose existing start/end for edit mode
  const startDecomp =
    isEdit && timeEntry.started_at
      ? decomposeDateTime(timeEntry.started_at)
      : null
  const endDecomp =
    isEdit && timeEntry.ended_at ? decomposeDateTime(timeEntry.ended_at) : null

  // Actual mode state
  const todayStr = timeEntry.date ?? getTodayStr()
  const [startDate, setStartDate] = useState(startDecomp?.date ?? todayStr)
  const [startHour, setStartHour] = useState(startDecomp?.hour ?? 8)
  const [startMinute, setStartMinute] = useState(startDecomp?.minute ?? 0)
  const [startAmPm, setStartAmPm] = useState<"AM" | "PM">(
    startDecomp?.ampm ?? "AM",
  )
  const [endDate, setEndDate] = useState(endDecomp?.date ?? todayStr)
  const [endHour, setEndHour] = useState(endDecomp?.hour ?? 5)
  const [endMinute, setEndMinute] = useState(endDecomp?.minute ?? 0)
  const [endAmPm, setEndAmPm] = useState<"AM" | "PM">(endDecomp?.ampm ?? "PM")

  // Block mode state
  const [blockDate, setBlockDate] = useState(startDecomp?.date ?? todayStr)
  const [blockHours, setBlockHours] = useState(
    Math.floor((timeEntry.admin_minutes ?? 0) / 60),
  )
  const [blockMinutes, setBlockMinutes] = useState(
    (timeEntry.admin_minutes ?? 0) % 60,
  )

  const { data, setData, post, put, processing, transform } = useForm({
    user_id: timeEntry.user_id,
    time_mode: timeEntry.time_mode ?? "actual",
    date: timeEntry.date,
    started_at: timeEntry.started_at,
    ended_at: timeEntry.ended_at,
    admin_minutes: timeEntry.admin_minutes ?? 0,
    service_item_type_id: timeEntry.service_item_type_id,
    travel_hours: timeEntry.travel_hours ?? 0,
    notes: timeEntry.notes ?? "",
    billable: true,
    mileage_to_job: timeEntry.mileage_to_job ?? 0,
    mileage_on_job: timeEntry.mileage_on_job ?? 0,
    mileage_from_job: timeEntry.mileage_from_job ?? 0,
    vehicle_allowance: timeEntry.vehicle_allowance || "",
  })

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = e.target.value ? parseInt(e.target.value) : null
    setData("service_item_type_id", typeId)
  }

  const submit: FormEventHandler = (e) => {
    e.preventDefault()

    transform((formData) => {
      const transformed = { ...formData }
      transformed.time_mode = timeMode

      if (timeMode === "actual") {
        transformed.started_at = buildDateTime(
          startDate,
          startHour,
          startMinute,
          startAmPm,
        )
        transformed.ended_at = buildDateTime(
          endDate,
          endHour,
          endMinute,
          endAmPm,
        )
        transformed.admin_minutes = 0
        transformed.date = startDate
      } else {
        transformed.started_at = `${blockDate}T00:00:00`
        transformed.ended_at = null as unknown as string
        transformed.admin_minutes = blockHours * 60 + blockMinutes
        transformed.date = blockDate
      }

      return { case_time_entry: transformed }
    })

    if (isEdit) {
      put(`/cases/${caseId}/time_entries/${timeEntry.id}`)
    } else {
      post(`/cases/${caseId}/time_entries`)
    }
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"
  const selectSmClass =
    "rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue text-sm"

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* 1. Time Type radio */}
      <div>
        <label className={labelClass}>Time Type</label>
        <div className="mt-2 flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="time_mode"
              value="actual"
              checked={timeMode === "actual"}
              onChange={() => setTimeMode("actual")}
              className="text-hpi-active-blue focus:ring-hpi-active-blue"
            />
            <span className="text-sm">Actual (Clock Times)</span>
          </label>
          {canUseBlockTime && (
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="time_mode"
                value="block"
                checked={timeMode === "block"}
                onChange={() => setTimeMode("block")}
                className="text-hpi-active-blue focus:ring-hpi-active-blue"
              />
              <span className="text-sm">Block (Duration)</span>
            </label>
          )}
        </div>
      </div>

      {/* 3. Time input section */}
      {timeMode === "actual" && (
        <div className="border-hpi-light-gray space-y-4 rounded border p-4">
          <h3 className="text-sm font-medium">Start Time</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Date
              </label>
              <input
                type="date"
                className={selectSmClass + " px-2 py-1"}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Hour
              </label>
              <select
                className={selectSmClass}
                value={startHour}
                onChange={(e) => setStartHour(parseInt(e.target.value))}
              >
                {clockHourOptions.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Min
              </label>
              <select
                className={selectSmClass}
                value={startMinute}
                onChange={(e) => setStartMinute(parseInt(e.target.value))}
              >
                {clockMinuteOptions.map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className={selectSmClass}
                value={startAmPm}
                onChange={(e) => setStartAmPm(e.target.value as "AM" | "PM")}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          <h3 className="text-sm font-medium">End Time</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Date
              </label>
              <input
                type="date"
                className={selectSmClass + " px-2 py-1"}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Hour
              </label>
              <select
                className={selectSmClass}
                value={endHour}
                onChange={(e) => setEndHour(parseInt(e.target.value))}
              >
                {clockHourOptions.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Min
              </label>
              <select
                className={selectSmClass}
                value={endMinute}
                onChange={(e) => setEndMinute(parseInt(e.target.value))}
              >
                {clockMinuteOptions.map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className={selectSmClass}
                value={endAmPm}
                onChange={(e) => setEndAmPm(e.target.value as "AM" | "PM")}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
          {errors?.started_at && (
            <p className={errorClass}>{errors.started_at[0]}</p>
          )}
          {errors?.ended_at && (
            <p className={errorClass}>{errors.ended_at[0]}</p>
          )}
        </div>
      )}

      {timeMode === "block" && (
        <div className="border-hpi-light-gray space-y-4 rounded border p-4">
          <h3 className="text-sm font-medium">Block Duration</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Date
              </label>
              <input
                type="date"
                className={selectSmClass + " px-2 py-1"}
                value={blockDate}
                onChange={(e) => setBlockDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Hours
              </label>
              <select
                className={selectSmClass}
                value={blockHours}
                onChange={(e) => setBlockHours(parseInt(e.target.value))}
              >
                {blockHourOptions.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Minutes
              </label>
              <select
                className={selectSmClass}
                value={blockMinutes}
                onChange={(e) => setBlockMinutes(parseInt(e.target.value))}
              >
                {blockMinuteOptions.map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {errors?.admin_minutes && (
            <p className={errorClass}>{errors.admin_minutes[0]}</p>
          )}
        </div>
      )}

      {/* 4. Service & Travel */}
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label className={labelClass}>Service Type</label>
          <select
            className={inputClass}
            value={data.service_item_type_id ?? ""}
            onChange={handleServiceTypeChange}
            required
          >
            <option disabled value="">
              --- SELECT ---
            </option>
            {serviceItemTypes.map((st) => (
              <option key={st.id} value={st.id}>
                {st.name}
              </option>
            ))}
          </select>
          {errors?.service_item_type_id && (
            <p className={errorClass}>{errors.service_item_type_id[0]}</p>
          )}
        </div>
      </div>

      {/* 5. Notes */}
      <div>
        <label className={labelClass}>Explain Time</label>
        <textarea
          className={inputClass}
          rows={4}
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          required
        />
        {errors?.notes && <p className={errorClass}>{errors.notes[0]}</p>}
      </div>

      {/* 6. Mileage */}
      <div>
        <h3 className="text-hpi-dark-gray mb-3 text-sm font-medium">
          Mileage & Travel (optional)
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-hpi-med-gray mb-1 block text-xs">
              Travel Time
            </label>
            <select
              className={inputClass}
              value={data.travel_hours}
              onChange={(e) =>
                setData("travel_hours", parseFloat(e.target.value))
              }
            >
              {travelHourOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-hpi-med-gray mb-1 block text-xs">
              Miles to Job
            </label>
            <input
              type="number"
              min="0"
              className={inputClass}
              value={data.mileage_to_job || ""}
              onChange={(e) =>
                setData("mileage_to_job", parseInt(e.target.value) || 0)
              }
            />
          </div>
          <div>
            <label className="text-hpi-med-gray mb-1 block text-xs">
              Miles OTJ
            </label>
            <input
              type="number"
              min="0"
              className={inputClass}
              value={data.mileage_on_job || ""}
              onChange={(e) =>
                setData("mileage_on_job", parseInt(e.target.value) || 0)
              }
            />
          </div>
          <div>
            <label className="text-hpi-med-gray mb-1 block text-xs">
              Miles from Job
            </label>
            <input
              type="number"
              min="0"
              className={inputClass}
              value={data.mileage_from_job || ""}
              onChange={(e) =>
                setData("mileage_from_job", parseInt(e.target.value) || 0)
              }
            />
          </div>
          <div>
            <label className="text-hpi-med-gray mb-1 block text-xs">
              Mileage Reimbursement
            </label>
            <select
              className={inputClass}
              value={data.vehicle_allowance}
              onChange={(e) => setData("vehicle_allowance", e.target.value)}
              required
            >
              <option disabled value="">
                --- SELECT ---
              </option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {errors?.vehicle_allowance && (
              <p className={errorClass}>{errors.vehicle_allowance[0]}</p>
            )}
          </div>
        </div>
      </div>

      {/* 7. Admin-Only Section */}
      {canViewFinancials && (
        <div className="space-y-4 rounded-lg border-2 border-amber-300 bg-amber-50/50 p-4">
          <div className="flex items-center gap-2 border-b border-amber-200 pb-2">
            <span className="rounded bg-amber-200 px-2 py-0.5 text-xs font-semibold tracking-wide text-amber-800 uppercase">
              Admin Only
            </span>
            <span className="text-xs text-amber-700">
              These fields are only visible to administrators and case managers
            </span>
          </div>

          {/* Employee selector (admin only) */}
          {canSelectEmployee && (
            <div>
              <label className={labelClass}>Report For Employee</label>
              <select
                className={inputClass}
                value={data.user_id ?? ""}
                onChange={(e) =>
                  setData(
                    "user_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                required
              >
                <option value="">Select employee...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              {errors?.user_id && (
                <p className={errorClass}>{errors.user_id[0]}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 8. Submit/Cancel */}
      <div className="flex justify-end space-x-3">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {processing
            ? "Saving..."
            : isEdit
              ? "Update Time Entry"
              : "Add Time Entry"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
